import type { WebauthnAssertionOptionsDto, WebauthnRegistrationOptionsDto } from '@fas/types';
import { base64url } from './device';

/*
 * WebAuthn user verification (Phase 3H, ADR-063): the device's own fingerprint, face or PIN
 * unlock confirms its user. Only a public key and signatures reach the server — never biometric
 * data. Binary fields travel as base64url.
 */

export function fromBase64url(text: string): Uint8Array<ArrayBuffer> {
  const b64 = text.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (text.length % 4)) % 4);
  const binary = atob(b64);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

const b64 = (buffer: ArrayBuffer) => base64url(new Uint8Array(buffer));

/** Whether this browser offers WebAuthn at all. */
export function webauthnSupported(): boolean {
  return typeof globalThis.PublicKeyCredential === 'function' && typeof navigator !== 'undefined' && !!navigator.credentials;
}

/** Whether the device can verify its user itself (fingerprint, face or PIN), as clock events need. */
export async function platformAuthenticatorAvailable(): Promise<boolean> {
  if (!webauthnSupported()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/** What the device answered instead of a credential, in words the person can act on. */
export class WebauthnProblem extends Error {
  constructor(
    message: string,
    /** The person cancelled, or the prompt timed out: asking again may well work. */
    readonly cancelled: boolean,
  ) {
    super(message);
    this.name = 'WebauthnProblem';
  }
}

function explain(err: unknown, what: 'setup' | 'confirm'): WebauthnProblem {
  const name = err instanceof DOMException || err instanceof Error ? err.name : '';
  if (name === 'NotAllowedError' || name === 'AbortError') {
    return new WebauthnProblem(what === 'setup' ? 'The set-up was cancelled or took too long. Try again.' : 'The confirmation was cancelled or took too long. Try again.', true);
  }
  if (name === 'InvalidStateError') return new WebauthnProblem('This device is already set up for your account.', false);
  if (name === 'SecurityError') return new WebauthnProblem('This address cannot use fingerprint or face confirmation. Open the attendance app at its usual address.', false);
  if (name === 'NotSupportedError') return new WebauthnProblem('This device cannot confirm you with a fingerprint, face or PIN.', false);
  return new WebauthnProblem(what === 'setup' ? 'This device could not be set up. Try again.' : 'Your device could not confirm you. Try again.', false);
}

const descriptors = (list: Array<{ type: 'public-key'; id: string; transports?: string[] }>): PublicKeyCredentialDescriptor[] =>
  list.map((c) => ({ type: c.type, id: fromBase64url(c.id), ...(c.transports ? { transports: c.transports as AuthenticatorTransport[] } : {}) }));

/** `navigator.credentials.create()` with the server's options; what the server needs to register the key. */
export async function createCredential(options: WebauthnRegistrationOptionsDto): Promise<{ id: string; clientDataJSON: string; attestationObject: string; transports?: string[] }> {
  let credential: Credential | null;
  try {
    credential = await navigator.credentials.create({
      publicKey: {
        challenge: fromBase64url(options.challenge),
        rp: options.rp,
        user: { id: fromBase64url(options.user.id), name: options.user.name, displayName: options.user.displayName },
        pubKeyCredParams: options.pubKeyCredParams,
        timeout: options.timeout,
        attestation: options.attestation,
        authenticatorSelection: options.authenticatorSelection,
        excludeCredentials: descriptors(options.excludeCredentials),
      },
    });
  } catch (err) {
    throw explain(err, 'setup');
  }
  if (!credential) throw new WebauthnProblem('The set-up was cancelled. Try again.', true);
  const key = credential as PublicKeyCredential;
  const response = key.response as AuthenticatorAttestationResponse;
  const transports = typeof response.getTransports === 'function' ? response.getTransports() : [];
  return {
    id: b64(key.rawId),
    clientDataJSON: b64(response.clientDataJSON),
    attestationObject: b64(response.attestationObject),
    ...(transports.length ? { transports } : {}),
  };
}

/** `navigator.credentials.get()` over a clock challenge: the device confirms its user. */
export async function getAssertion(options: WebauthnAssertionOptionsDto): Promise<{ id: string; clientDataJSON: string; authenticatorData: string; signature: string; userHandle: string | null }> {
  let credential: Credential | null;
  try {
    credential = await navigator.credentials.get({
      publicKey: {
        challenge: fromBase64url(options.challenge),
        rpId: options.rpId,
        timeout: options.timeout,
        userVerification: options.userVerification,
        allowCredentials: descriptors(options.allowCredentials),
      },
    });
  } catch (err) {
    throw explain(err, 'confirm');
  }
  if (!credential) throw new WebauthnProblem('The confirmation was cancelled. Try again.', true);
  const key = credential as PublicKeyCredential;
  const response = key.response as AuthenticatorAssertionResponse;
  return {
    id: b64(key.rawId),
    clientDataJSON: b64(response.clientDataJSON),
    authenticatorData: b64(response.authenticatorData),
    signature: b64(response.signature),
    userHandle: response.userHandle ? b64(response.userHandle) : null,
  };
}
